/**
 * All traders — ROADMAP 9.12. Owner only.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE SCREEN FOR THE CAPABILITY TABLE, WHICH HAD NO SCREEN AT ALL
 *
 * `UserExecutionPolicy` is read on the ORDER PATH: it decides whether the
 * dispatcher may slice a leg past the exchange freeze limit, re-price a
 * resting order until it fills, or net a chain click against a manual leg.
 * Until now every one of those was set by hand against the database, which
 * means nobody could answer "who has slicing on" without a psql session.
 *
 * COMPOSITION ONLY, per the structure rule. The fetch and the optimistic
 * write are in `hooks/useTraderPolicies`, the filtering and the confirmation
 * wording are pure functions in `utils/traderFilter`, and every control is
 * its own component.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS SCREEN DELIBERATELY DOES NOT DO
 *
 * Create, delete or re-role a login, or reset a password. This administers
 * CAPABILITIES. Identity has a different blast radius — a re-role to VIEWER
 * stops someone trading mid-session — and it does not belong behind the same
 * inline toggles as a slicing switch.
 *
 * THE ADMIN'S OWN ROW IS IN THE LIST, unflagged except for a "you" tag. It
 * was asked for, and there is no case for a separate panel: the capabilities
 * mean the same thing for the owner as for anyone else, and a second surface
 * for the same table is a second thing to keep in step.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Loader2, Search } from 'lucide-react'
import {
  CAPABILITY_LABEL,
  EXECUTION_CAPABILITIES,
  type ExecutionCapability,
  type TraderPolicyDto,
} from '@apna/shared'
import { useAuth } from '@/store/auth'
import { Input } from '@/components/ui/input'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'
import { useTraderPolicies } from './hooks/useTraderPolicies'
import { bulkWarning, capabilityCounts, filterTraders, NO_FILTER } from './utils/traderFilter'
import { TraderRow } from './components/TraderRow'
import { BulkBar } from './components/BulkBar'

interface PendingBulk {
  readonly capability: ExecutionCapability
  readonly on: boolean
  readonly targets: readonly TraderPolicyDto[]
}

export default function Traders() {
  const me = useAuth((s) => s.user)
  const owner = me?.role === 'OWNER'
  const { traders, loading, error, saving, refusals, update, refresh } = useTraderPolicies(owner)

  const [filter, setFilter] = useState(NO_FILTER)
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [bulk, setBulk] = useState<PendingBulk | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)

  const shown = useMemo(() => filterTraders(traders, filter), [traders, filter])
  const counts = useMemo(() => capabilityCounts(traders), [traders])

  if (!owner) return <Navigate to="/" replace />

  /*
   * The selection is intersected with what is ON SCREEN before any bulk
   * action. Typing in the search box after ticking rows would otherwise
   * apply a capability to logins the admin can no longer see — the selection
   * is a set of ids and does not know the filter moved under it.
   */
  const selected = shown.filter((t) => picked.has(t.userId))

  const toggleAll = (on: boolean) =>
    setPicked(on ? new Set(shown.map((t) => t.userId)) : new Set())

  const applyBulk = async () => {
    if (bulk === null) return
    setBulkBusy(true)
    /*
     * SEQUENTIALLY, not `Promise.all`. These are writes to the order-path
     * capability table; firing twenty at once gives twenty interleaved
     * upserts whose log lines cannot be read in order, and the list is short
     * enough that the wait is imperceptible. A failure stops nothing — every
     * row reports its own refusal.
     */
    for (const t of bulk.targets) {
      await update(t.userId, { [bulk.capability]: bulk.on })
    }
    setBulkBusy(false)
    setBulk(null)
    setPicked(new Set())
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-[1180px] space-y-5">
        <header className="space-y-1.5">
          <h1 className="text-xl font-semibold">All traders</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            What each login&apos;s order path is allowed to do. These are read by the dispatcher at
            order time — a change here takes effect on the next order, with no restart.
          </p>
          <p className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-[11.5px] text-muted-foreground">
            <span className="tabular-nums">{traders.length} logins</span>
            {EXECUTION_CAPABILITIES.map((c) => (
              <span key={c} className="tabular-nums">
                {CAPABILITY_LABEL[c]}{' '}
                <span className={cn('font-semibold', counts[c] > 0 ? 'text-primary' : 'text-muted-foreground')}>
                  {counts[c]} on
                </span>
              </span>
            ))}
          </p>
        </header>

        {error === null ? null : (
          <p role="alert" className="rounded border border-destructive/40 p-3 text-sm text-destructive">
            {error}{' '}
            <button type="button" onClick={refresh} className="underline">
              Try again
            </button>
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filter.query}
              onChange={(e) => setFilter((f) => ({ ...f, query: e.target.value }))}
              placeholder="Search a login, or a capability that is on…"
              aria-label="Search traders"
              data-testid="trader-search"
              className="h-8 pl-8 text-[12.5px]"
            />
          </div>
          <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground">
            <input
              type="checkbox"
              checked={filter.withAccountsOnly}
              onChange={(e) => setFilter((f) => ({ ...f, withAccountsOnly: e.target.checked }))}
              data-testid="filter-with-accounts"
              className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
            />
            Only logins with a broker account
          </label>
        </div>

        <BulkBar
          count={selected.length}
          busy={bulkBusy}
          onApply={(capability, on) => setBulk({ capability, on, targets: selected })}
          onClear={() => setPicked(new Set())}
        />

        <div className="overflow-x-auto rounded-lg border border-border">
          {/*
            ─────────────────────────────────────────────────────────────────
            `table-fixed` WITH DECLARED WIDTHS, AND THE LOGIN TAKES THE REST

            Auto layout sized every column to its CONTENT, so the widest
            headers — "AUTO-PRICE", "CHASE FOR" — took the space and the one
            column with something to read lost it: at 22 logins the name
            rendered as "A…" and the address as "ahmed@…".

            The lesson is the drawer's, learned over five attempts: when a
            table has one column that must stay legible and the rest are
            fixed-shape controls, fix the widths and let the readable column
            have everything left over. 774px of declared width inside a
            1,020px floor leaves the login 246px at the narrowest and the
            whole surplus on a real monitor.
            ─────────────────────────────────────────────────────────────────
          */}
          <table className="w-full min-w-[1096px] table-fixed border-collapse text-left">
            <colgroup>
              <col className="w-9" />
              {/* The LOGIN column is the only one with no width: it gets the
                  remainder, which is the entire point of this layout. */}
              <col />
              <col className="w-16" />
              <col className="w-[52px]" />
              <col className="w-[52px]" />
              <col className="w-[68px]" />
              <col className="w-24" />
              <col className="w-[76px]" />
              <col className="w-[86px]" />
              <col className="w-24" />
              <col className="w-[72px]" />
              <col className="w-[76px]" />
              <col className="w-[76px]" />
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">
                <th scope="col" className="py-2 pl-3 pr-1">
                  <input
                    type="checkbox"
                    aria-label="Select every trader shown"
                    data-testid="select-all"
                    checked={shown.length > 0 && selected.length === shown.length}
                    onChange={(e) => toggleAll(e.target.checked)}
                    className="h-3.5 w-3.5 cursor-pointer accent-[hsl(var(--primary))]"
                  />
                </th>
                <th scope="col" className="py-2 pr-3">Login</th>
                <th scope="col" className="py-2 pr-3">Role</th>
                <th scope="col" className="py-2 pr-3 text-right" title="Live broker connections">Accts</th>
                <th scope="col" className="py-2 pr-4 text-right" title="Trades not yet closed">Book</th>
                <th scope="col" className="py-2 pr-2">Slicing</th>
                <th scope="col" className="py-2 pr-4 text-right" title="Ceiling in units on one leg">Max qty</th>
                <th scope="col" className="py-2 pr-4 text-right" title="How many orders one leg may become. Paced at 5/sec, so the count is seconds of release.">Max orders</th>
                <th scope="col" className="py-2 pr-2">Auto-price</th>
                <th scope="col" className="py-2 pr-4 text-right" title="How long one order may be chased">Chase for</th>
                <th scope="col" className="py-2 pr-4">Netting</th>
                <th scope="col" className="py-2 pr-3 text-right" title="Slippage cap through the touch">Buffer</th>
                <th scope="col" className="py-2 pr-3 text-right">Changed</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="p-6 text-center text-sm text-muted-foreground">
                    <Loader2 size={14} className="mx-auto mb-2 animate-spin" />
                    Reading the capability table…
                  </td>
                </tr>
              ) : shown.length === 0 ? (
                <tr>
                  <td colSpan={13} className="p-6 text-center text-sm text-muted-foreground">
                    {traders.length === 0
                      ? 'No logins on this deployment.'
                      : 'No login matches that search.'}
                  </td>
                </tr>
              ) : (
                shown.map((t) => (
                  <TraderRow
                    key={t.userId}
                    trader={t}
                    isSelf={t.userId === me?.id}
                    selected={picked.has(t.userId)}
                    busy={saving.has(t.userId) || bulkBusy}
                    refusal={refusals.get(t.userId)}
                    onSelect={(on) =>
                      setPicked((prev) => {
                        const next = new Set(prev)
                        if (on) next.add(t.userId)
                        else next.delete(t.userId)
                        return next
                      })
                    }
                    onChange={(patch) => void update(t.userId, patch)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <p className="text-[11.5px] leading-relaxed text-muted-foreground">
          <strong className="font-semibold text-foreground">Slicing</strong> splits one leg into
          several orders so it clears the exchange freeze limit, which is a cap per ORDER rather
          than per position; its ceiling is the blast radius.{' '}
          <strong className="font-semibold text-foreground">Auto-price</strong> modifies a resting
          order toward the touch until it fills, bounded so it cannot follow a running market to
          the close. <strong className="font-semibold text-foreground">Netting</strong> makes a
          chain click on a strike already held reduce that manual leg instead of opening a second
          one; strategy and combined-premium baskets are never netted. A login with no row has
          every capability off and has never been granted one.
        </p>
      </div>

      <ConfirmDialog
        open={bulk !== null}
        onOpenChange={(open) => {
          if (!open) setBulk(null)
        }}
        title={
          bulk === null
            ? ''
            : `${bulk.on ? 'Switch on' : 'Switch off'} ${CAPABILITY_LABEL[bulk.capability].toLowerCase()}?`
        }
        description={bulk === null ? '' : bulkWarning(bulk.targets, bulk.capability, bulk.on)}
        confirmLabel={bulk?.on === true ? 'Switch on' : 'Switch off'}
        isPending={bulkBusy}
        pendingLabel="Applying…"
        onConfirm={() => void applyBulk()}
      />
    </div>
  )
}
